import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import RefreshIcon from '@material-ui/icons/RefreshRounded';
import NamedBox from '../atoms/NamedBox';
import ProgressLine from '../atoms/ProgressLine';
import NilValuesTooltip from '../molecules/NilValuesTooltip';
import On3LogoIcon from '../icons/On3Icon';
import { COLOR_BACKGROUND_WARM, COLOR_GREEN, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import Player from '../types/Player';
import { on3NilValuesResponse } from '../types/PlayerNILValues';
import { datetimeToDate } from '../services/converter';
import Chip from '@material-ui/core/Chip';
import { Divider } from '@material-ui/core';
import ArrowUpIcon from '../icons/ArrowUpIcon';
import ArrowDownIcon from '../icons/ArrowDownIcon';
import Team from '../types/Team';
import NotAvailable from '../atoms/NotAvailable';
import SocialXIcon from '../icons/SocialXIcon';
import SocialInstagramIcon from '../icons/SocialInstagramIcon';
import SocialTikTokIcon from '../icons/SocialTikTokIcon';

interface On3NilValuationProps {
  className?: string;
  player?: Player;
  playerNilValues?: on3NilValuesResponse;
  playerOn3Date?: string;
  evaluationTeam?: Team | null;
  
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    border: `1px solid ${COLOR_MIDDLE_GRAY}`,
  },
  headerContainer:{
    background: COLOR_BACKGROUND_WARM,
    padding: theme.spacing(1),
    alignItems: 'center',
  },
  bodyContainer:{
    display: 'flex',
    flexDirection: 'row',
    columnGap: theme.spacing(1),
    alignItems: 'stretch',
    padding: theme.spacing(0, 2)
  },
  chip: {
    fontWeight: 600,
    height: 24,
  },
  positionChip: {
    color: COLOR_WHITE,
  },
  borderRight: {
    borderRight: '1px solid #eeeeee',
    // display: 'flex',
    alignItems: 'stretch',
  },
  pad: {
    padding: theme.spacing(2, 0),
  },
  pad10: {
    padding: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    fontFamily: FONT_PROXIMA_NOVA,
  },
  on3Icon: {
    width: 40,
    height: 'auto',
    marginRight: theme.spacing(1.5),
  },
  headerText: {
    fontWeight: 700,
  },
  restartIcon: {
    color: COLOR_TEXT,
    fontSize: theme.typography.pxToRem(20),
  },
  headerCenterCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  averagesHeaderCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
    fontWeight: 700,
  },
  row: {
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${COLOR_MIDDLE_GRAY}`,

    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  rowTitle: {
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(14),
  },
  rowVisibility: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    textTransform: 'uppercase',
    color: COLOR_TEXT,
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  valueWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'center',
  },
  mainValue: {
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 700,
    color: COLOR_GREEN,
  },
  dateText: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_TEXT,
  },
  barsWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  barRow: {
    display: 'flex',
    alignItems: 'center',
  },
  barLabel: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    marginRight: theme.spacing(1),
    minWidth: 28,
  },
  barMeta: {
    marginLeft: theme.spacing(6),
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_TEXT,
  },
  progressLine: {
    paddingLeft: 0,
  },
  averagesHeader: {
    fontSize: theme.typography.pxToRem(12),
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
  },
  averageRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: theme.typography.pxToRem(11),
    color: COLOR_TEXT,
  },
  averageLabel: {
    fontWeight: 500,
  },
  averageValue: {
    fontWeight: 700,
  },
  logoLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    lineHeight: 1.2,
  },
  socialRow: {
    borderTop: `1px solid ${COLOR_MIDDLE_GRAY}`,
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    alignItems: 'center',
  },
  socialItem: {
    flex: '1 1 200px',
    fontSize: theme.typography.pxToRem(18),
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  socialLabel: {
    fontWeight: 400,
    marginRight: theme.spacing(0.5),
  },
  socialValue: {
    fontWeight: 700,
  },
  socialLabelWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  socialIcon: {
    display: 'inline-flex',
    alignItems: 'center',

    '& svg': {
      width: 30,
      height: 30,
    },
  },
  tiktokInfoWrap: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
  },
  tiktokTextWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(14),
  },
  socialStatRow: {
    display: 'flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
  },
  socialStatLabel: {
    fontWeight: 400,
  },
  socialStatValue: {
    fontWeight: 700,
  },
  footer: {
    display: 'flex',
    justifyContent: 'center',
    background: COLOR_BACKGROUND_WARM,
    padding: theme.spacing(2),
    alignItems: 'center',
  },
  nilHistoryIndicator: {
    height: '40px',
    position: 'relative',
    right: '10px',
    width: '40px',
  },
}), { name: 'On3NilValuation' });

function formatCurrencyShort(value?: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  const abs = Math.abs(value);

  if (abs >= 1_000_000) {
    return `$${(value / 1_000_000).toFixed(1)}M`;
  }

  if (abs >= 1_000) {
    return `$${(value / 1_000).toFixed(1)}K`;
  }

  return `$${value.toLocaleString()}`;
}

function formatCurrencyTwoDecimals(value?: number | null): string {
  if (value === null || value === undefined) return 'N/A';
  return `$${value.toFixed(2)}`;
}

export default function On3NilValuation(props: On3NilValuationProps) {
  const {
    className,
    playerNilValues,
    evaluationTeam,
    player
  } = props;
  const classes = useStyles();
  const playerAny = player as any;

  const xFollowers = player?.twitterFollowers;
  const xAvgLikes = playerAny?.twitterAvgLikes ?? playerAny?.xAvgLikes ?? playerAny?.xAverageLikes;
  const instagramFollowers = player?.instagramFollowers;
  const instagramAvgLikes = playerAny?.instagramAvgLikes ?? playerAny?.instagramAverageLikes;
  const tikTokFollowers = player?.tikTokFollowers;
  const tikTokAvgLikes = playerAny?.tikTokAvgLikes ?? playerAny?.tiktokAvgLikes ?? playerAny?.tikTokAverageLikes ?? playerAny?.tiktokAverageLikes;

  const playerData = playerNilValues;

  const rows = [
    {
      id: 'valuation',
      title: 'NIL Valuation',
      visibility: 'Public',
      positionCode: playerData?.positionCode,
      value: playerData?.on3PublicNILValuation?.value,
      direction: playerData?.on3PublicNILValuation?.direction,
      allPercentile: playerData?.on3PublicNILValuation?.allPercentile,
      allRank: playerData?.on3PublicNILValuation?.allRank,
      allRankTotal: playerData?.on3PublicNILValuation?.allRankTotal,
      positionPercentile: playerData?.on3PublicNILValuation?.positionPercentile,
      positionRank: playerData?.on3PublicNILValuation?.positionRank,
      positionRankTotal: playerData?.on3PublicNILValuation?.positionRankTotal,
      teamAllAverageValue: playerData?.on3PublicNILValuation?.teamAllAverageValue,
      teamAllRank: playerData?.on3PublicNILValuation?.teamAllRank,
      teamAllRankTotal: playerData?.on3PublicNILValuation?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3PublicNILValuation?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3PublicNILValuation?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3PublicNILValuation?.teamPositionRankTotal,
    },
    {
      id: 'roster',
      title: 'Roster Value',
      visibility: 'Private',
      positionCode: playerData?.positionCode,
      value: playerData?.on3RosterValue?.value,
      direction: playerData?.on3RosterValue?.direction,
      allPercentile: playerData?.on3RosterValue?.allPercentile,
      allRank: playerData?.on3RosterValue?.allRank,
      allRankTotal: playerData?.on3RosterValue?.allRankTotal,
      positionPercentile: playerData?.on3RosterValue?.positionPercentile,
      positionRank: playerData?.on3RosterValue?.positionRank,
      positionRankTotal: playerData?.on3RosterValue?.positionRankTotal,
      teamAllAverageValue: playerData?.on3RosterValue?.teamAllAverageValue,
      teamAllRank: playerData?.on3RosterValue?.teamAllRank,
      teamAllRankTotal: playerData?.on3RosterValue?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3RosterValue?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3RosterValue?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3RosterValue?.teamPositionRankTotal,
    },
    {
      id: 'nil',
      title: 'NIL Value',
      visibility: 'Private',
      positionCode: playerData?.positionCode,
      value: playerData?.on3PrivateNILValue?.value,
      direction: playerData?.on3PrivateNILValue?.direction,
      allPercentile: playerData?.on3PrivateNILValue?.allPercentile,
      allRank: playerData?.on3PrivateNILValue?.allRank,
      allRankTotal: playerData?.on3PrivateNILValue?.allRankTotal,
      positionPercentile: playerData?.on3PrivateNILValue?.positionPercentile,
      positionRank: playerData?.on3PrivateNILValue?.positionRank,
      positionRankTotal: playerData?.on3PrivateNILValue?.positionRankTotal,
      teamAllAverageValue: playerData?.on3PrivateNILValue?.teamAllAverageValue,
      teamAllRank: playerData?.on3PrivateNILValue?.teamAllRank,
      teamAllRankTotal: playerData?.on3PrivateNILValue?.teamAllRankTotal,
      teamPositionAverageValue: playerData?.on3PrivateNILValue?.teamPositionAverageValue,
      teamPositionRank: playerData?.on3PrivateNILValue?.teamPositionRank,
      teamPositionRankTotal: playerData?.on3PrivateNILValue?.teamPositionRankTotal,
    },
  ];

  return (
    <>
    <div className={classes.root}>
      {
        rows.map((row, idx) => (
          <div key={idx}>
            <Grid container className={classes.headerContainer}>
              <Grid item xs={12} md={2}>
                <div className={classes.header}>
                  <On3LogoIcon className={classes.on3Icon} width={2000} viewBox={'0 0 2500 2500'} />
                  <span className={classes.headerText}>{row.title}</span>
                </div>
              </Grid>
              <Grid item xs={12} md={3}>{row.visibility}</Grid>
              <Grid item xs={12} md={2} className={classes.headerCenterCell}>
                <RefreshIcon className={classes.restartIcon} />
              </Grid>
              <Grid item xs={12} md={1} className={classes.averagesHeaderCell}>
                <img
                  src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                  alt={evaluationTeam?.name || 'Team Logo'}
                  width={20}
                  height={20}
                />
                <span>Averages</span>
              </Grid>
              <Grid item xs={12} md={1} />
              <Grid item xs={12} md={2} />
            </Grid>
            <Grid container className={clsx(classes.bodyContainer, classes.borderRight)} alignItems="stretch">
              {/* vertically position center this div */} 
              <Grid item xs={12} md={1} className={clsx(classes.borderRight, classes.pad)}>
                <div className={classes.valueWrapper}>
                  <span>${row.value}</span>
                  {
                    row?.direction === 'up' ?
                    <ArrowUpIcon className={clsx(classes.nilHistoryIndicator)} />
                    :
                    <ArrowDownIcon className={clsx(classes.nilHistoryIndicator)} />
                  }
                </div>
              </Grid>
              <Grid item xs={12} md={4} className={clsx(classes.barsWrapper, classes.borderRight, classes.pad)}>
                <div className={classes.barRow}>
                  <span className={classes.barLabel}>
                    <Chip label={'All'} className={clsx(classes.chip, classes.positionChip)} color="primary" />
                  </span>
                  <ProgressLine
                    className={classes.progressLine}
                    value={row.allPercentile || 0}
                  />
                </div>
                <div className={classes.barRow}>
                  <span className={classes.barLabel}>
                      <Chip label={row.positionCode} className={clsx(classes.chip, classes.positionChip)} color="primary" />
                  </span>
                  <ProgressLine
                    className={classes.progressLine}
                    value={row.positionPercentile || 0}
                  />
                </div>
              </Grid>
              <Grid item xs={12} md={2} className={clsx(classes.borderRight, classes.pad)}>
                <div>Rank: #{row.allRank} of {row.allRankTotal}</div>
                <div>Rank: #{row.positionRank} of {row.positionRankTotal}</div>
              </Grid>
              <Grid item xs={12} md={1} className={clsx(classes.borderRight, classes.pad)}>
                <div>{formatCurrencyTwoDecimals(row.teamAllAverageValue)}</div>
                <div>{formatCurrencyTwoDecimals(row.teamPositionAverageValue)}</div>
              </Grid>
              <Grid item xs={12} md={1} className={clsx(classes.borderRight, classes.pad)}>
                <div className={classes.logoLabelRow}>
                  <img
                    src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                    alt={evaluationTeam?.name || 'Team Logo'}
                    width={20}
                    height={20}
                  />
                  Players
                </div>
                <div className={classes.logoLabelRow}>
                  <img
                    src={evaluationTeam?.logo247 || evaluationTeam?.logoESPN || ''}
                    alt={evaluationTeam?.name || 'Team Logo'}
                    width={20}
                    height={20}
                  />
                  {row.positionCode}
                </div>
              </Grid>
              <Grid item xs={12} md={2} className={clsx(classes.pad)}>
                <div>Rank: #{row.teamAllRank || 0}/{row.teamAllRankTotal || 0}</div>
                <div>Rank: #{row.teamPositionRank || 0}/{row.teamPositionRankTotal || 0}</div>
              </Grid>
            </Grid>
          </div>
        ))
      }
      <div className={classes.socialRow}>
        <div className={classes.socialItem}>
          <div className={classes.tiktokInfoWrap}>
            <SocialXIcon className={classes.socialIcon} />
            <div className={classes.tiktokTextWrap}>
              <div className={classes.socialStatRow}>
                <span className={classes.socialStatLabel}>Followers:</span>
                <span className={classes.socialStatValue}>{xFollowers ?? <NotAvailable />}</span>
              </div>
            </div>
          </div>
        </div>
        <Divider orientation="vertical" flexItem />
        <div className={classes.socialItem}>
          <div className={classes.tiktokInfoWrap}>
            <SocialInstagramIcon className={classes.socialIcon} />
            <div className={classes.tiktokTextWrap}>
              <div className={classes.socialStatRow}>
                <span className={classes.socialStatLabel}>Followers:</span>
                <span className={classes.socialStatValue}>{instagramFollowers ?? <NotAvailable />}</span>
              </div>
              <div className={classes.socialStatRow}>
                <span className={classes.socialStatLabel}>Avg Likes:</span>
                <span className={classes.socialStatValue}>{instagramAvgLikes ?? <NotAvailable />}</span>
              </div>
            </div>
          </div>
        </div>
        <Divider orientation="vertical" flexItem />
        <div className={classes.socialItem}>
          <div className={classes.tiktokInfoWrap}>
            <SocialTikTokIcon className={classes.socialIcon} />
            <div className={classes.tiktokTextWrap}>
              <div className={classes.socialStatRow}>
                <span className={classes.socialStatLabel}>Subscribers:</span>
                <span className={classes.socialStatValue}>{tikTokFollowers ?? <NotAvailable />}</span>
              </div>
              <div className={classes.socialStatRow}>
                <span className={classes.socialStatLabel}>Avg Likes:</span>
                <span className={classes.socialStatValue}>{tikTokAvgLikes ?? <NotAvailable />}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={classes.footer}>
        <div>
          <span>All data provided by <u>Total Player Value.</u> </span>
        </div>
      </div>
    </div>
    </>
  );
}

